import { defineStore } from "pinia";
import axios from "axios";
import store from "@/state/store";

// Endpoints de "connectivity check" — respostas minúsculas (204 / poucos bytes),
// pensadas exatamente para testar se existe internet. Provedores diferentes de
// propósito, para uma queda de um deles não virar "sem internet".
// Não usar www.msftconnecttest.com: o certificado não cobre o host, o https
// falha sempre e a versão http é bloqueada como mixed content.
const URLS_INTERNET = [
  "https://connectivitycheck.gstatic.com/generate_204",
  "https://www.cloudflare.com/cdn-cgi/trace",
  "https://detectportal.firefox.com/success.txt",
];

const TIMEOUT_PROBE = 5000;
const INTERVALO_MONITOR = 15000;
const INTERVALO_MINIMO_RELOAD = 60000;
const CHAVE_ULTIMO_RELOAD = "serverErrorUltimoReload";

// Ficam fora do state: são handles de browser, não dados reativos.
let monitorInterval = null;
let onBrowserOnline = null;

// Endpoint público e leve da própria API (200, texto de 63 bytes). Não usar a
// raiz `.../api`: ela responde 302 para `http://.../api/`, e o downgrade de
// esquema é bloqueado como mixed content a partir de uma página https.
function urlServidor() {
  const base = axios.defaults.baseURL || process.env.VUE_APP_ETOTEM_API || "/";
  return base.replace(/\/+$/, "") + "/publico/version";
}

function comTimeout(url, options) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), TIMEOUT_PROBE);

  return fetch(url, {
    cache: "no-store",
    signal: controller.signal,
    ...options,
  }).finally(() => clearTimeout(timeout));
}

// Para os endpoints de internet só interessa saber se a conexão chegou a ser
// estabelecida, por isso `no-cors`: a resposta é opaca, mas o fetch só rejeita
// quando não houve resposta alguma (DNS, conexão recusada, TLS, timeout) — o
// mesmo cenário do "Network Error" do axios.
async function probeAlcancavel(url) {
  try {
    await comTimeout(url, { mode: "no-cors" });
    return true;
  } catch (error) {
    return false;
  }
}

export const useErrorStore = defineStore("error", {
  state: () => ({
    errorStatus: {
      hasError: true,
      errorMessage: [],
      status: null,
    },
    serverError: false,
    verificandoServidor: false,
  }),

  getters: {},

  actions: {
    errorHandler(error, global = false) {
      const errorStatus = {
        hasError: true,
        errorMessage: [],
        status: null,
      };

      errorStatus.hasError = true;
      if (error.response) {
        errorStatus.errorMessage = error.response.data.errors
          ? error.response.data.errors
          : [error.response.data];
        errorStatus.status = error.response.status;
      } else {
        errorStatus.errorMessage = [error];
      }

      if (!global) {
        this.errorStatus = { ...errorStatus };

        setTimeout(() => {
          const el = document.getElementById("error-container");

          if (!el) return;
          this.scrollToEl(el);
        });

        return;
      }

      // Falha da impressora local não diz nada sobre o servidor.
      if (error.config?.params?.apiImpressora) return;

      const isNetworkError = errorStatus.errorMessage.some(
        (e) => e.message?.toString().toUpperCase() == "NETWORK ERROR",
      );

      if (isNetworkError) {
        this.checkOnline();
      } else {
        // O servidor respondeu (4xx/5xx): está de pé.
        this.setServerError(false);
      }
    },

    cleanError() {
      this.errorStatus.hasError = false;
      this.errorStatus.errorMessage = [];
      this.errorStatus.status = null;
      this.setServerError(false);
    },

    onValidationError(event) {
      this.scrollToEl(event.$el);
    },

    scrollToEl(el) {
      el.scrollIntoView({
        behavior: "smooth",
        block: "end",
        inline: "nearest",
      });
    },

    // Existe internet? `navigator.onLine` resolve o caso óbvio sem rede nenhuma;
    // os probes rodam em paralelo (o antigo loop sequencial podia levar minutos).
    async checkInternet() {
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        return false;
      }

      const resultados = await Promise.all(
        URLS_INTERNET.map((url) => probeAlcancavel(url)),
      );
      return resultados.some(Boolean);
    },

    // A API está respondendo? A API libera CORS refletindo o Origin, então aqui
    // dá para ler o status em vez de só testar se a conexão abriu: 5xx é o
    // ingress respondendo sozinho com a aplicação fora. Qualquer status < 500
    // conta como de pé — inclusive 404, caso o endpoint não exista numa versão
    // mais antiga da API (senão a tela de manutenção nunca sairia).
    // O header idSessao é obrigatório aqui: o RequestInterceptor da API cria
    // uma linha em `sessao` para todo request /publico/ que chegue sem ele.
    async checkServidor() {
      try {
        const response = await comTimeout(urlServidor(), {
          mode: "cors",
          headers: { idSessao: store.state.sessao.idSessao || "health-probe" },
        });
        return response.status < 500;
      } catch (error) {
        return false;
      }
    },

    // Só é "manutenção" quando existe internet E a API não responde.
    // Se a API responde, o Network Error foi transitório e não vale a tela cheia.
    async checkOnline() {
      const [internet, servidor] = await Promise.all([
        this.checkInternet(),
        this.checkServidor(),
      ]);

      this.setServerError(internet && !servidor);

      return { internet, servidor };
    },

    setServerError(valor) {
      if (valor) {
        this.serverError = true;
        this.startServerMonitor();
      } else {
        this.serverError = false;
        this.stopServerMonitor();
      }
    },

    // Enquanto a tela de manutenção estiver no ar, ninguém mais dispara requisição
    // (o overlay cobre tudo e telas como a TV perdem o loop de polling ao falhar),
    // então o monitor é quem descobre que o servidor voltou.
    startServerMonitor() {
      if (monitorInterval) return;

      monitorInterval = setInterval(() => {
        this.verificarRetornoServidor();
      }, INTERVALO_MONITOR);

      onBrowserOnline = () => this.verificarRetornoServidor();
      window.addEventListener("online", onBrowserOnline);
    },

    stopServerMonitor() {
      if (monitorInterval) {
        clearInterval(monitorInterval);
        monitorInterval = null;
      }

      if (onBrowserOnline) {
        window.removeEventListener("online", onBrowserOnline);
        onBrowserOnline = null;
      }

      this.verificandoServidor = false;
    },

    async verificarRetornoServidor() {
      if (this.verificandoServidor || !this.serverError) return;

      this.verificandoServidor = true;
      try {
        if (await this.checkServidor()) this.servidorVoltou();
      } finally {
        this.verificandoServidor = false;
      }
    },

    // Recarrega para que todas as telas voltem do zero: os loops de polling que
    // morreram no erro de rede não se recuperam sozinhos. O intervalo mínimo
    // evita ciclo de reload quando a rede fica oscilando.
    servidorVoltou() {
      this.setServerError(false);

      if (!this.podeRecarregar()) return;

      try {
        sessionStorage.setItem(CHAVE_ULTIMO_RELOAD, String(Date.now()));
      } catch (error) {
        // sessionStorage indisponível (modo restrito): segue sem a guarda.
      }

      location.reload();
    },

    podeRecarregar() {
      try {
        const ultimo = Number(sessionStorage.getItem(CHAVE_ULTIMO_RELOAD)) || 0;
        return Date.now() - ultimo > INTERVALO_MINIMO_RELOAD;
      } catch (error) {
        return true;
      }
    },
  },
});
